import React, { useEffect, useState } from 'react'
import { getBookingNotes, getDealDemos, setBookingAttendance, setDemoAttendance, setDemoTranscript, getDealBookings, updateBooking, type DealDemo, type Booking } from './crmApi'
import { IconCalendarUi, IconLink, IconNote, IconPhone, crmToast } from './crmUi'
import { BookingModal, QuickBookButton } from './BookingModal'
import { addFollowUpTodoOnce } from '../todo/useTodos'
import { useAuth } from '../../contexts/AuthContext'

/** Follow-up task text for a cancelled booking — used on the lead AND the to-do list. */
export const followUpText = (b: Booking) =>
  `Follow up: cancelled ${b.type} "${b.title}"${b.startAt ? ` (was ${new Date(b.startAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })})` : ''}`

/**
 * Meetings tab — every Calendly meeting for this lead, upcoming and past: time, who booked,
 * the Google Meet link, the transcript link (manual for now; the Google integration will fill it
 * automatically later), how the booking matched, and the invitee's answers to the Calendly
 * booking questions. Read-only toward Calendly — this is our record of what happened.
 */

const fmtRange = (startAt: string | null, endAt: string | null) => {
  if (!startAt) return 'Unknown time'
  const s = new Date(startAt)
  const day = s.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  const st = s.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const en = endAt ? new Date(endAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null
  return `${day} · ${st}${en ? `–${en}` : ''}`
}

/** SOURCE CHIP (Lee, 21 Oct 2026): next to every status, say where the row
 *  came from — that's what decides its vocabulary. Calendly demos read
 *  Held/No-show (attendance); calendar + CRM bookings read Done (completed). */
const SourceChip: React.FC<{ kind: 'calendly' | 'calendar' | 'crm' | 'crm-call' }> = ({ kind }) => {
  const meta = {
    calendly: { label: 'CALENDLY DEMO', tip: 'Booked through Calendly. Past demos read HELD or NO-SHOW (attendance), never Done.' },
    calendar: { label: 'CALENDAR', tip: 'Imported from Google Calendar. Reads DONE automatically once the event has run; CANCELLED if the event was cancelled on Google.' },
    crm: { label: 'CRM BOOKING', tip: 'Booked in the CRM. Reads DONE when completed (auto once its calendar event ends, or ticked by hand).' },
    'crm-call': { label: 'CRM CALL', tip: 'A scheduled call booked in the CRM. Reads DONE when completed with a logged remark.' },
  }[kind]
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-surface-tertiary text-txt-secondary border border-border cursor-help" title={meta.tip}>
      {meta.label}
    </span>
  )
}

const STATUS_STYLE: Record<string, { label: string; bg: string; fg: string }> = {
  upcoming: { label: 'Upcoming', bg: '#DCFCE7', fg: '#166534' },
  held: { label: 'Held', bg: '#E0E7FF', fg: '#3730A3' },
  cancelled: { label: 'Cancelled', bg: '#FEE2E2', fg: '#991B1B' },
}

/** Editable attendance on a PAST demo — replaces the old clock-only "Held" chip.
 *  Auto value comes from the Meet join records; clicking overrides it by hand. */
const DemoAttendanceChip: React.FC<{ demo: DealDemo; onChanged: () => void }> = ({ demo, onChanged }) => {
  const [editing, setEditing] = useState(false)
  const s = demo.attendanceStatus
  const chip = s === 'no_show'
    ? { label: 'NO-SHOW', bg: '#FEE2E2', fg: '#991B1B' }
    : s === 'held'
      ? { label: 'HELD', bg: '#DCFCE7', fg: '#166534' }
      : { label: 'Held', bg: '#E0E7FF', fg: '#3730A3' } // clock assumption — attendance not verified yet
  const save = (v: 'held' | 'no_show' | null) => {
    setEditing(false)
    setDemoAttendance(demo.id, v)
      .then(() => { crmToast(v ? `Marked ${v === 'held' ? 'Held' : 'No-Show'}` : 'Attendance cleared', 'success'); onChanged() })
      .catch(() => crmToast('Could not update attendance', 'error'))
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={() => setEditing((v) => !v)}
        className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
        style={{ background: chip.bg, color: chip.fg }}
        title={s ? `Set ${demo.attendanceSource === 'auto' ? 'automatically from the Meet join records' : 'manually'} — click to change` : 'Attendance not verified (time-based only) — click to set Held / No-Show'}
      >
        {chip.label} ▾
      </button>
      {editing && (
        <span className="inline-flex items-center gap-1">
          <button onClick={() => save('held')} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Held</button>
          <button onClick={() => save('no_show')} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border border-red-300 text-red-700 hover:bg-red-50">No-Show</button>
          {s && <button onClick={() => save(null)} className="px-1.5 py-0.5 rounded text-[10px] text-txt-tertiary border border-border hover:bg-surface-hover" title="Clear — auto-detection may re-set it">Clear</button>}
        </span>
      )}
    </span>
  )
}

const MeetingRow: React.FC<{ demo: DealDemo; onChanged: () => void }> = ({ demo, onChanged }) => {
  const [showQ, setShowQ] = useState(false)
  const [editingT, setEditingT] = useState(false)
  const [tUrl, setTUrl] = useState(demo.transcriptUrl || '')
  const [showNotes, setShowNotes] = useState(false)
  const kind = demo.status === 'cancelled' ? 'cancelled' : demo.upcoming ? 'upcoming' : 'held'
  const s = STATUS_STYLE[kind]

  const saveTranscript = () => {
    const url = tUrl.trim() || null
    setDemoTranscript(demo.id, url)
      .then(() => { crmToast(url ? 'Transcript link saved' : 'Transcript link cleared', 'success'); setEditingT(false); onChanged() })
      .catch(() => crmToast('Could not save — is it a valid URL?', 'error'))
  }

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14px] font-semibold text-txt-primary">{demo.eventName}</p>
            {kind === 'held' ? (
              <DemoAttendanceChip demo={demo} onChanged={onChanged} />
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
            )}
            <SourceChip kind="calendly" />
          </div>
          <p className="text-[13px] text-txt-secondary mt-0.5">{fmtRange(demo.startAt, demo.endAt)}</p>
          {(demo.inviteeName || demo.inviteeEmail) && (
            <p className="text-[12px] text-txt-tertiary mt-0.5">
              Booked by {demo.inviteeName || demo.inviteeEmail}{demo.inviteeName && demo.inviteeEmail ? ` · ${demo.inviteeEmail}` : ''}
              {demo.matchedBy === 'domain' && <span> · matched by company domain</span>}
            </p>
          )}
          {(demo.hostName || demo.hostEmail) && (
            <p className="text-[12px] text-txt-tertiary mt-0.5">
              Host: <span className="font-medium text-txt-secondary">{demo.hostName || demo.hostEmail}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {demo.meetUrl && kind === 'upcoming' && (
            <a href={demo.meetUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: '#2F6FB0' }}>
              Join Google Meet
            </a>
          )}
          {demo.meetUrl && kind !== 'upcoming' && (
            <a href={demo.meetUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-brand-600 hover:underline">Meet link</a>
          )}
          {demo.transcriptUrl && !editingT ? (
            <a href={demo.transcriptUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover">
              Transcript
            </a>
          ) : null}
        </div>
      </div>

      {/* Booking questions the invitee answered on Calendly */}
      {demo.questions.length > 0 && (
        <div className="px-4 pb-2">
          <button onClick={() => setShowQ((v) => !v)} className="text-[11px] font-medium text-brand-600 hover:underline">
            {showQ ? 'Hide' : 'Show'} booking answers ({demo.questions.length})
          </button>
          {showQ && (
            <div className="mt-2 space-y-2 rounded-lg bg-surface-secondary p-3">
              {demo.questions.map((q, i) => (
                <div key={i}>
                  <p className="text-[11px] font-semibold text-txt-secondary">{q.question}</p>
                  <p className="text-[12px] text-txt-primary whitespace-pre-wrap">{q.answer || '—'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gemini meeting notes — pulled automatically from the host's calendar once the
          meeting is held. The doc link sits in Transcript above; the text is readable here. */}
      {demo.notesText && (
        <div className="px-4 pb-2">
          <button onClick={() => setShowNotes((v) => !v)} className="text-[11px] font-medium text-brand-600 hover:underline">
            {showNotes ? 'Hide' : 'Show'} Gemini meeting notes
          </button>
          {showNotes && (
            <div className="mt-2 rounded-lg bg-surface-secondary p-3 max-h-80 overflow-y-auto">
              <p className="text-[12px] text-txt-primary whitespace-pre-wrap">{demo.notesText}</p>
            </div>
          )}
        </div>
      )}

      {/* Transcript link — auto-filled by the Gemini notes job; manual entry stays as the
          fallback for meetings where Gemini did not run. */}
      <div className="px-4 py-2 border-t border-border bg-surface-secondary/50 flex items-center gap-2">
        {editingT ? (
          <>
            <input
              value={tUrl}
              onChange={(e) => setTUrl(e.target.value)}
              placeholder="https://… (Google Doc / Drive transcript link)"
              className="flex-1 min-w-0 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400"
            />
            <button onClick={saveTranscript} className="text-[11px] font-semibold text-brand-600">Save</button>
            <button onClick={() => { setEditingT(false); setTUrl(demo.transcriptUrl || '') }} className="text-[11px] text-txt-tertiary">Cancel</button>
          </>
        ) : demo.notesText || demo.transcriptUrl ? (
          <p className="text-[11px] text-txt-tertiary">
            {demo.notesText ? 'Notes pulled automatically from Gemini.' : 'Transcript attached.'}{' '}
            <button onClick={() => setEditingT(true)} className="hover:text-txt-primary underline">Edit link</button>
          </p>
        ) : (
          <p className="text-[11px] text-txt-tertiary">
            Notes pull automatically after the meeting (Gemini).{' '}
            <button onClick={() => setEditingT(true)} className="hover:text-txt-primary underline">Add link manually</button>
          </p>
        )}
      </div>
    </div>
  )
}

/** A scheduled booking (our own Call/Meeting scheduler — PRD-04). Calls REQUIRE a
 * logged remark before they can be marked done (server-enforced) — the ✓ Done button
 * on a call opens the log box instead of completing silently. Planned bookings are
 * EDITABLE (Google gets patched + re-notifies); cancelled ones offer follow-up
 * actions (task on lead + to-do, or book a follow-up). */
/**
 * Held vs No-Show (Lee, 11 Aug 2026): auto-populated from Meet participant
 * records (only-WeTech-joined ⇒ no-show), editable here — a manual choice is
 * never overwritten by the sweep. Shown on past, non-cancelled meetings.
 */
const AttendanceChip: React.FC<{ b: Booking; onChanged: () => void }> = ({ b, onChanged }) => {
  const [editing, setEditing] = useState(false)
  const started = !!b.startAt && new Date(b.startAt).getTime() < Date.now()
  if (b.type !== 'meeting' || !started || b.status === 'cancelled') return null
  const s = b.attendanceStatus
  const chip = s === 'held'
    ? { label: 'HELD', bg: '#DCFCE7', fg: '#166534' }
    : s === 'no_show'
      ? { label: 'NO-SHOW', bg: '#FEE2E2', fg: '#991B1B' }
      : { label: 'ATTENDANCE ?', bg: '#F1F5F9', fg: '#64748B' }
  const save = (v: 'held' | 'no_show' | null) => {
    setEditing(false)
    setBookingAttendance(b.id, v)
      .then(() => { crmToast(v ? `Marked ${v === 'held' ? 'Held' : 'No-Show'}` : 'Attendance cleared', 'success'); onChanged() })
      .catch(() => crmToast('Could not update attendance', 'error'))
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={() => setEditing((v) => !v)}
        className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
        style={{ background: chip.bg, color: chip.fg }}
        title={`${s ? `Set ${b.attendanceSource === 'auto' ? 'automatically from the Meet join records' : 'manually'}` : 'Not determined yet'} — click to change`}
      >
        {chip.label} ▾
      </button>
      {editing && (
        <span className="inline-flex items-center gap-1">
          <button onClick={() => save('held')} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Held</button>
          <button onClick={() => save('no_show')} className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border border-red-300 text-red-700 hover:bg-red-50">No-Show</button>
          {s && <button onClick={() => save(null)} className="px-1.5 py-0.5 rounded text-[10px] text-txt-tertiary border border-border hover:bg-surface-hover" title="Clear — the sweep may re-detect">Clear</button>}
        </span>
      )}
    </span>
  )
}

/**
 * Gemini notes on a CRM-booked meeting — PRIVATE to whoever's Gemini recorded it
 * (Lee, 11 Aug 2026): the recorder sees the full notes + doc link; everyone else
 * sees only that private notes exist and whose they are.
 */
/** Gemini notes on a CRM-booked meeting — VISIBLE TO EVERYONE (Lee, 9 Oct
 *  2026; owner-only rule retired). Two people recorded? Two accordions, one
 *  per recorder. */
const BookingNotesBlock: React.FC<{ b: Booking; viewerEmail: string | null }> = ({ b }) => {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState<Array<{ owner: string | null; docUrl: string | null; notesText: string | null }> | null>(null)
  const [openIdx, setOpenIdx] = useState(0)
  return (
    <div className="mt-1.5">
      <button
        onClick={() => {
          if (!open && !notes) getBookingNotes(b.id).then((r: any) => setNotes(r.notes || [])).catch(() => crmToast('Could not load the notes', 'error'))
          setOpen((v) => !v)
        }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      >
        <IconNote className="w-3.5 h-3.5" /> {open ? 'Hide' : 'Show'} Gemini notes{b.notesOwner ? ` · ${b.notesOwner}` : ''}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5">
          {!notes && <p className="text-[11px] text-txt-tertiary">Loading…</p>}
          {notes && !notes.length && <p className="text-[11px] text-txt-tertiary">No readable notes on this meeting.</p>}
          {(notes || []).map((n, i) => (
            <div key={i} className="rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                className="w-full flex items-center justify-between px-2.5 py-1.5 bg-surface-secondary text-left"
              >
                <span className="text-[11px] font-bold text-txt-primary">Notes by {n.owner || 'unknown recorder'}</span>
                <span className="text-[11px] text-txt-tertiary">{openIdx === i ? '▾' : '▸'}</span>
              </button>
              {openIdx === i && (
                <div className="p-2.5 max-h-64 overflow-y-auto border-t border-border">
                  {n.docUrl && <a href={n.docUrl} target="_blank" rel="noreferrer" className="text-[11px] text-brand-600 font-semibold hover:underline">Open the doc ↗</a>}
                  <p className="text-[12px] text-txt-primary whitespace-pre-wrap mt-1">{n.notesText || '(empty)'}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const BookingRow: React.FC<{ b: Booking; onChanged: () => void; onEdit: (b: Booking) => void; onFollowUpBook: (b: Booking) => void; dealId: string }> = ({ b, onChanged, onEdit, onFollowUpBook }) => {
  const { currentAgent } = useAuth()
  const [logging, setLogging] = useState(false)
  const [logText, setLogText] = useState('')
  const [logLink, setLogLink] = useState('')

  const setStatus = (status: 'done' | 'cancelled' | 'planned') =>
    updateBooking(b.id, { status })
      .then(() => {
        if (status === 'cancelled') {
          // Server has already created the lead's follow-up task (marked NEXT);
          // add the personal to-do half when it is MY booking.
          if (currentAgent?.id && String(b.assigneeId) === String(currentAgent.id)) {
            addFollowUpTodoOnce(String(currentAgent.id), b.id, followUpText(b))
          }
          crmToast('Cancelled — follow-up task added to the lead (set as Next)', 'success')
        } else {
          crmToast(status === 'done' ? 'Marked done' : 'Reopened', 'success')
        }
        onChanged()
      })
      .catch(() => crmToast('Could not update the booking', 'error'))

  const submitLog = () => {
    const text = logText.trim()
    if (!text) { crmToast('Log what happened on the call first', 'error'); return }
    const links = logLink.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^https?:\/\//.test(s))
    updateBooking(b.id, { status: 'done', logText: text, logLinks: links })
      .then(() => { crmToast('Call logged', 'success'); setLogging(false); onChanged() })
      .catch(() => crmToast('Could not log the call', 'error'))
  }

  return (
    <div className="rounded-xl border border-border px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[14px] font-semibold text-txt-primary">{b.type === 'call' ? <IconPhone className="w-3.5 h-3.5 inline -mt-0.5" /> : <IconCalendarUi className="w-3.5 h-3.5 inline -mt-0.5" />} {b.title}</p>
          {b.overdue && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-white" style={{ background: '#DC2626' }}>Overdue</span>}
          {b.status === 'done' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: '#DCFCE7', color: '#166534' }} title="This booking completed — automatic once its calendar event ends.">Done</span>}
          {b.status === 'cancelled' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: '#FEE2E2', color: '#991B1B' }} title="The event was cancelled (on Google Calendar or in the CRM) — it never happened.">Cancelled</span>}
          <SourceChip kind={b.createdBy === 'meeting-mirror' ? 'calendar' : b.type === 'call' ? 'crm-call' : 'crm'} />
          {b.recordingUrl && (
            <a href={b.recordingUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
              title="Google Meet recording — opens in Drive">
              ▶ Recording
            </a>
          )}
          {b.transcriptUrl && (
            <a href={b.transcriptUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
              title="Meet transcript — opens the Google Doc">
              Transcript
            </a>
          )}
          <AttendanceChip b={b} onChanged={onChanged} />
        </div>
        <p className="text-[13px] text-txt-secondary mt-0.5">{fmtRange(b.startAt, b.endAt)}</p>
        {b.type === 'meeting' && b.attendees.length > 0 && (
          <p className="text-[12px] text-txt-tertiary mt-0.5 truncate">Attendees: {b.attendees.join(', ')}</p>
        )}
        {b.type === 'meeting' && b.status === 'planned' && !b.meetUrl && (
          <p className="text-[11px] text-txt-tertiary mt-0.5">No Calendar event — the Google write may have failed for this booking (check the activity feed).</p>
        )}
        {b.note && <p className="text-[12px] text-txt-tertiary mt-0.5 whitespace-pre-wrap">{b.note}</p>}
        {b.hasNotes && <BookingNotesBlock b={b} viewerEmail={currentAgent?.email || null} />}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {b.type === 'call' && b.phone && b.status === 'planned' && (
          <a href={`tel:${b.phone.replace(/[\s()-]/g, '')}`} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700">
            <IconPhone className="w-3.5 h-3.5" /> {b.phone}
          </a>
        )}
        {b.type === 'meeting' && b.meetUrl && b.status === 'planned' && (
          <a href={b.meetUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: '#2F6FB0' }}>
            Join Meet
          </a>
        )}
        {b.status === 'planned' ? (
          <>
            <button onClick={() => onEdit(b)} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover" title="Edit — Google is updated and attendees re-notified">
              ✎ Edit
            </button>
            <button
              onClick={() => (b.type === 'call' ? setLogging((v) => !v) : setStatus('done'))}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover"
            >
              ✓ Done
            </button>
            <button onClick={() => setStatus('cancelled')} className="px-2 py-1.5 rounded-lg text-xs text-txt-tertiary hover:text-error" title="Cancel booking">✕</button>
          </>
        ) : b.status === 'cancelled' ? (
          /* Follow-up task is AUTOMATIC (lead task marked NEXT + to-do entry) — the
             replacement booking is the one manual action. No Reopen anywhere (Lee):
             cancelled/done bookings are history; the way forward is a new booking. */
          <button onClick={() => onFollowUpBook(b)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700" title="Book a replacement meeting, attendees prefilled">
            Book follow-up
          </button>
        ) : null}
      </div>

      {/* Mandatory call log — what happened + optional links (recording, quote, doc). */}
      {logging && (
        <div className="w-full mt-1 rounded-lg bg-surface-secondary border border-border p-3 space-y-2">
          <p className="text-[11px] font-semibold text-txt-primary">Log the call (required to mark it done)</p>
          <textarea
            value={logText}
            onChange={(e) => setLogText(e.target.value)}
            placeholder="What happened, outcome, next steps…"
            rows={3}
            autoFocus
            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none"
          />
          <input
            value={logLink}
            onChange={(e) => setLogLink(e.target.value)}
            placeholder="Attach links (optional): https://… — separate multiple with spaces"
            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400"
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setLogging(false)} className="px-2.5 py-1.5 rounded-lg text-xs text-txt-secondary hover:bg-surface-hover">Cancel</button>
            <button onClick={submitLog} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700">Save log & mark done</button>
          </div>
        </div>
      )}

      {/* The saved log, on completed calls */}
      {b.status === 'done' && b.logText && (
        <div className="w-full mt-1 rounded-lg bg-surface-secondary/60 border border-border px-3 py-2">
          <p className="text-[11px] font-semibold text-txt-tertiary uppercase tracking-wide">Call log</p>
          <p className="text-[12px] text-txt-primary whitespace-pre-wrap mt-0.5">{b.logText}</p>
          {b.logLinks.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-1">
              {b.logLinks.map((l, i) => (
                <a key={i} href={l} target="_blank" rel="noreferrer" className="text-[11px] text-brand-600 hover:underline truncate max-w-[260px]"><IconLink className="w-3 h-3 inline -mt-0.5" /> {l}</a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const DealMeetings: React.FC<{ dealId: string; dealName?: string }> = ({ dealId, dealName }) => {
  const [demos, setDemos] = useState<DealDemo[] | null>(null)
  const [booked, setBooked] = useState<Booking[] | null>(null)
  const [editing, setEditing] = useState<Booking | null>(null)
  const [followingUp, setFollowingUp] = useState<Booking | null>(null)
  const load = () => {
    getDealDemos(dealId).then(setDemos).catch(() => setDemos([]))
    getDealBookings(dealId).then(setBooked).catch(() => setBooked([]))
  }
  useEffect(load, [dealId])
  const rowProps = { onChanged: load, onEdit: setEditing, onFollowUpBook: setFollowingUp, dealId }

  if (demos === null && booked === null) return <p className="text-sm text-txt-tertiary">Loading meetings…</p>

  // FULL history stays here regardless of what the dock/board hide — and always
  // latest → oldest (Lee, 19 Aug 2026). Sorted client-side so the order is
  // guaranteed even if a source returns rows unsorted.
  const byLatest = <T extends { startAt: string | null }>(rows: T[]) =>
    [...rows].sort((a, b) => (b.startAt ? new Date(b.startAt).getTime() : 0) - (a.startAt ? new Date(a.startAt).getTime() : 0))
  const upcoming = byLatest((demos || []).filter((d) => d.upcoming && d.status !== 'cancelled'))
  const past = byLatest((demos || []).filter((d) => !d.upcoming || d.status === 'cancelled'))
  const planned = byLatest((booked || []).filter((b) => b.status === 'planned'))
  const finished = byLatest((booked || []).filter((b) => b.status !== 'planned'))
  const empty = upcoming.length === 0 && past.length === 0 && planned.length === 0 && finished.length === 0

  return (
    <div className="max-w-[780px] space-y-5">
      {/* Scheduler — book a call or meeting from right here (PRD-04). */}
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary">Scheduled ({planned.length})</p>
        <QuickBookButton dealId={dealId} dealName={dealName} onSaved={load} />
      </div>
      {planned.length > 0 && <div className="space-y-3">{planned.map((b) => <BookingRow key={b.id} b={b} {...rowProps} />)}</div>}

      {empty && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="text-sm text-txt-secondary">No meetings for this lead yet.</p>
          <p className="text-[11px] text-txt-tertiary mt-1">Book a call or meeting with the button above — Calendly bookings also land here automatically.</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Upcoming ({upcoming.length})</p>
          <div className="space-y-3">{upcoming.map((d) => <MeetingRow key={d.id} demo={d} onChanged={load} />)}</div>
        </div>
      )}
      {finished.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Done & cancelled bookings ({finished.length})</p>
          <div className="space-y-3">{finished.map((b) => <BookingRow key={b.id} b={b} {...rowProps} />)}</div>
        </div>
      )}
      {past.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Past & cancelled ({past.length})</p>
          <div className="space-y-3">{past.map((d) => <MeetingRow key={d.id} demo={d} onChanged={load} />)}</div>
        </div>
      )}

      {/* Edit an existing booking — Google patched + attendees re-notified. */}
      {editing && (
        <BookingModal dealId={dealId} dealName={dealName} editBooking={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
      {/* Book a follow-up for a cancelled booking — details prefilled from the original. */}
      {followingUp && (
        <BookingModal
          dealId={dealId}
          dealName={dealName}
          initialType={followingUp.type}
          initial={{ title: `Follow-up: ${followingUp.title}`, attendees: followingUp.attendees, note: followingUp.note || undefined }}
          onClose={() => setFollowingUp(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}
